import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation, useStaggerAnimation } from "@/hooks/useScrollAnimation";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

const positions = [
  {
    id: "helper",
    title: "訪問介護員",
    titleEn: "Home Care Helper",
    types: ["正社員", "登録ヘルパー"],
    description:
      "ご利用者様のご自宅を訪問し、身体介護・生活援助などのサービスを提供していただきます。利用者様の生活を支える、やりがいのある仕事です。未経験の方も丁寧に研修を行いますので、安心してご応募ください。",
    duties: ["身体介護（入浴・排泄・食事介助など）", "生活援助（掃除・洗濯・調理など）", "通院等乗降介助", "記録・報告業務"],
    requirements: ["介護職員初任者研修修了以上（または取得見込み）", "普通自動車免許（AT限定可）", "思いやりを持って利用者様に接することができる方", "未経験・ブランクのある方も歓迎"],
    conditions: [
      { label: "雇用形態", value: "正社員 / 登録ヘルパー" },
      { label: "勤務地", value: "愛知県刈谷市およびその周辺" },
      { label: "勤務時間", value: "シフト制（相談可）" },
      { label: "給与", value: "時給 1,100円〜（経験・資格により優遇）" },
      { label: "待遇・福利厚生", value: "社会保険完備、交通費支給、資格取得支援制度あり" },
    ],
    color: "bg-[#e8f8fb]",
    accent: "text-[#3ab5c6]",
    border: "border-[#3ab5c6]",
    img: "https://readdy.ai/api/search-image?query=japanese%20female%20care%20worker%20in%20professional%20uniform%20visiting%20elderly%20woman%20at%20home%2C%20bright%20living%20room%2C%20warm%20natural%20light%2C%20friendly%20smile%2C%20helping%20with%20daily%20activities%2C%20photorealistic&width=800&height=500&seq=recruit-helper-01&orientation=landscape",
  },
  {
    id: "supervisor",
    title: "サービス提供責任者",
    titleEn: "Service Coordinator",
    types: ["正社員", "登録ヘルパー"],
    description:
      "訪問介護サービスの計画立案・スタッフ管理・利用者様との調整など、チームのリーダーとして活躍していただきます。介護の現場経験を活かしてキャリアアップしたい方に最適なポジションです。",
    duties: [
      "訪問介護計画書の作成・管理",
      "ヘルパースタッフへの指導・育成",
      "利用者様・ご家族・ケアマネジャーとの連絡調整",
      "サービス品質の管理・改善",
    ],
    requirements: [
      "介護福祉士、または実務者研修修了者",
      "訪問介護員としての実務経験3年以上",
      "普通自動車免許（AT限定可）",
      "リーダーシップを発揮できる方",
    ],
    conditions: [
      { label: "雇用形態", value: "正社員 / 登録ヘルパー" },
      { label: "勤務地", value: "愛知県刈谷市およびその周辺" },
      { label: "勤務時間", value: "9:00〜18:00（シフト制・相談可）" },
      { label: "給与", value: "月給 200,000円〜（経験・資格により優遇）" },
      { label: "待遇・福利厚生", value: "社会保険完備、交通費支給、資格取得支援制度あり" },
    ],
    color: "bg-[#fff8f0]",
    accent: "text-orange-500",
    border: "border-orange-400",
    img: "https://readdy.ai/api/search-image?query=japanese%20professional%20care%20coordinator%20woman%20at%20desk%20reviewing%20documents%2C%20bright%20modern%20office%2C%20warm%20natural%20light%2C%20confident%20professional%20expression%2C%20organized%20workspace%2C%20photorealistic&width=800&height=500&seq=recruit-supervisor-01&orientation=landscape",
  },
];

const benefits = [
  { icon: "ri-graduation-cap-line", title: "資格取得支援", desc: "介護職員初任者研修・実務者研修の費用を会社が全額サポートします。" },
  { icon: "ri-time-line", title: "柔軟な勤務時間", desc: "ライフスタイルに合わせたシフト調整が可能。育児中の方も活躍中です。" },
  { icon: "ri-shield-check-line", title: "社会保険完備", desc: "健康保険・厚生年金・雇用保険・労災保険を完備しています。" },
  { icon: "ri-car-line", title: "交通費全額支給", desc: "通勤・訪問にかかる交通費は全額支給します。" },
  { icon: "ri-team-line", title: "充実した研修制度", desc: "入職時研修から定期的なスキルアップ研修まで、成長をサポートします。" },
  { icon: "ri-heart-line", title: "アットホームな職場", desc: "スタッフ同士の距離が近く、困ったときはすぐに相談できる環境です。" },
];

export default function RecruitPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activePosition, setActivePosition] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: msgRef, isVisible: msgVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: benefitRef, visibleItems: benefitItems } = useStaggerAnimation(benefits.length, { threshold: 0.1 });
  const { ref: posRef, visibleItems: posItems } = useStaggerAnimation(positions.length, { threshold: 0.05 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.05 });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d7g6t3mi0ais34q5lt10", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[340px] md:h-[420px] overflow-hidden">
        <img
          src="https://static.readdy.ai/image/e643b173cc54a8b3f926f18f1e409a57/22b6314595b839edac87321819e90acc.jpeg"
          alt="株式会社FD 採用情報 訪問介護員・サービス提供責任者募集"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div
          className="absolute inset-0 flex items-center justify-center"
          ref={heroRef as React.RefObject<HTMLDivElement>}
        >
          <div className="text-center">
            <p
              className="text-white/70 text-xs font-['Poppins'] tracking-widest mb-3"
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.7s ease, transform 0.7s ease",
              }}
            >
              RECRUIT
            </p>
            <h1
              className="text-3xl md:text-5xl font-bold text-white tracking-wide"
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(25px)",
                transition: "opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s",
              }}
            >
              採用情報
            </h1>
            <div
              className="flex items-center justify-center gap-2 mt-4 text-xs text-white/60"
              style={{
                opacity: heroVisible ? 1 : 0,
                transition: "opacity 0.8s ease 0.3s",
              }}
            >
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">TOP</Link>
              <span>›</span>
              <span className="text-white/90">採用情報</span>
            </div>
          </div>
        </div>
      </section>

      {/* Message */}
      <section className="py-20 bg-white">
        <div className="max-w-[900px] mx-auto px-6">
          <div
            ref={msgRef as React.RefObject<HTMLDivElement>}
            className="flex flex-col lg:flex-row items-center gap-10"
            style={{
              opacity: msgVisible ? 1 : 0,
              transform: msgVisible ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <div className="flex-1 text-center lg:text-left">
              <p className="text-xs font-['Poppins'] tracking-widest text-[#3ab5c6] mb-2">MESSAGE</p>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 leading-snug">
                あなたの「ありがとう」が、<br className="hidden sm:block" />
                誰かの毎日を支えています。
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed max-w-[680px] mx-auto lg:mx-0">
                ヘルパーステーションみりんでは、利用者様一人ひとりに寄り添った介護を大切にしています。
                スタッフが安心して長く働ける環境づくりに力を入れており、資格取得支援や柔軟な勤務体制など、
                働きやすさを追求しています。介護の仕事に興味がある方、経験を活かしてキャリアアップしたい方、
                ぜひ私たちと一緒に働きませんか。
              </p>
            </div>
            {/* マスコットキャラクター */}
            <div
              className="shrink-0 w-48 h-48 md:w-64 md:h-64 md:ml-4"
              style={{
                animation: msgVisible ? "mascotBounce 3.5s ease-in-out infinite" : "none",
              }}
            >
              <img
                src="https://static.readdy.ai/image/e643b173cc54a8b3f926f18f1e409a57/b0c003e41dae743172da41a9da2f4e91.png"
                alt="ヘルパーステーションみりん マスコットキャラクター"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
        <style>{`
          @keyframes mascotBounce {
            0%, 100% { transform: translateY(0) rotate(3deg); }
            50% { transform: translateY(-10px) rotate(3deg); }
          }
        `}</style>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#f7fcfd]">
        <div className="max-w-[1240px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-['Poppins'] tracking-widest text-[#3ab5c6] mb-1">BENEFITS</p>
            <h2 className="text-2xl font-bold text-gray-800">働く環境・福利厚生</h2>
          </div>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            ref={benefitRef as React.RefObject<HTMLDivElement>}
          >
            {benefits.map((b, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 flex gap-4 items-start"
                style={{
                  opacity: benefitItems[i] ? 1 : 0,
                  transform: benefitItems[i] ? "translateY(0)" : "translateY(30px)",
                  transition: `opacity 0.7s ease ${i * 0.08}s, transform 0.7s ease ${i * 0.08}s`,
                }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#e8f8fb] shrink-0">
                  <i className={`${b.icon} text-lg text-[#3ab5c6]`}></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-1">{b.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Positions */}
      <section className="py-20 bg-white">
        <div className="max-w-[1240px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-['Poppins'] tracking-widest text-[#3ab5c6] mb-1">POSITIONS</p>
            <h2 className="text-2xl font-bold text-gray-800">募集職種</h2>
          </div>

          <div
            className="space-y-10"
            ref={posRef as React.RefObject<HTMLDivElement>}
          >
            {positions.map((pos, i) => (
              <div
                key={pos.id}
                className="rounded-2xl overflow-hidden border border-gray-100"
                style={{
                  opacity: posItems[i] ? 1 : 0,
                  transform: posItems[i] ? "translateY(0)" : "translateY(40px)",
                  transition: `opacity 0.8s ease ${i * 0.15}s, transform 0.8s ease ${i * 0.15}s`,
                }}
              >
                {/* Card Header */}
                <div className="relative h-56 md:h-72 overflow-hidden">
                  <img
                    src={pos.img}
                    alt={pos.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/20"></div>
                  <div className="absolute inset-0 flex items-end p-8">
                    <div>
                      <p className={`text-xs font-['Poppins'] tracking-widest mb-1 text-white/70`}>{pos.titleEn}</p>
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">{pos.title}</h3>
                      <div className="flex gap-2 flex-wrap">
                        {pos.types.map((t) => (
                          <span key={t} className="text-xs border border-white/60 text-white px-3 py-1 rounded-full whitespace-nowrap">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className={`${pos.color} p-8`}>
                  <p className="text-sm text-gray-700 leading-relaxed mb-8">{pos.description}</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                    {/* 業務内容 */}
                    <div>
                      <h4 className={`text-sm font-bold ${pos.accent} mb-3 flex items-center gap-2`}>
                        <i className="ri-list-check-2 text-base"></i> 主な業務内容
                      </h4>
                      <ul className="space-y-2">
                        {pos.duties.map((d, j) => (
                          <li key={j} className="flex items-start gap-2 text-xs text-gray-600">
                            <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${pos.accent.replace("text-", "bg-")}`}></span>
                            {d}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 応募要件 */}
                    <div>
                      <h4 className={`text-sm font-bold ${pos.accent} mb-3 flex items-center gap-2`}>
                        <i className="ri-user-star-line text-base"></i> 応募要件
                      </h4>
                      <ul className="space-y-2">
                        {pos.requirements.map((r, j) => (
                          <li key={j} className="flex items-start gap-2 text-xs text-gray-600">
                            <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${pos.accent.replace("text-", "bg-")}`}></span>
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* 勤務条件 */}
                  <div className="bg-white rounded-xl p-5">
                    <h4 className={`text-sm font-bold ${pos.accent} mb-4 flex items-center gap-2`}>
                      <i className="ri-file-list-3-line text-base"></i> 勤務条件
                    </h4>
                    <div className="space-y-3">
                      {pos.conditions.map((c, j) => (
                        <div key={j} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs">
                          <span className={`font-medium ${pos.accent} shrink-0 w-28`}>{c.label}</span>
                          <span className="text-gray-600">{c.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => setActivePosition(pos.id)}
                    className={`mt-6 inline-flex items-center gap-2 text-sm font-medium px-7 py-3 rounded-full border-2 ${pos.border} ${pos.accent} hover:bg-white transition-colors cursor-pointer whitespace-nowrap`}
                  >
                    <i className="ri-mail-send-line"></i>
                    この職種に応募する
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply-form" className="py-20 bg-[#f7fcfd]">
        <div className="max-w-[760px] mx-auto px-6">
          <div
            ref={formRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: formVisible ? 1 : 0,
              transform: formVisible ? "translateY(0)" : "translateY(40px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <div className="text-center mb-10">
              <p className="text-xs font-['Poppins'] tracking-widest text-[#3ab5c6] mb-1">APPLY</p>
              <h2 className="text-2xl font-bold text-gray-800">採用応募フォーム</h2>
              <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                ご応募いただきありがとうございます。<br className="hidden sm:block" />
                下記フォームにご記入の上、送信してください。担当者よりご連絡いたします。
              </p>
            </div>

            {submitted ? (
              <div className="bg-[#e8f8fb] rounded-2xl p-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#3ab5c6] mx-auto mb-5">
                  <i className="ri-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">応募が完了しました</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  ご応募ありがとうございます。<br />
                  内容を確認の上、担当者よりご連絡いたします。
                </p>
                <Link
                  to="/"
                  className="inline-block mt-8 bg-[#3ab5c6] text-white text-sm px-8 py-3 rounded-full hover:bg-[#2da0b0] transition-colors cursor-pointer whitespace-nowrap"
                >
                  トップページへ戻る
                </Link>
              </div>
            ) : (
              <form
                data-readdy-form
                id="recruit-form"
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-8 md:p-10 space-y-6"
              >
                {/* 応募職種 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    応募職種 <span className="text-rose-500 text-xs ml-1">必須</span>
                  </label>
                  <select
                    name="position"
                    required
                    value={activePosition ?? ""}
                    onChange={(e) => setActivePosition(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#3ab5c6] transition-colors cursor-pointer bg-white"
                  >
                    <option value="">選択してください</option>
                    <option value="訪問介護員">訪問介護員</option>
                    <option value="サービス提供責任者">サービス提供責任者</option>
                  </select>
                </div>

                {/* 雇用形態 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    希望雇用形態 <span className="text-rose-500 text-xs ml-1">必須</span>
                  </label>
                  <div className="flex gap-6">
                    {["正社員", "登録ヘルパー"].map((t) => (
                      <label key={t} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                          type="radio"
                          name="employment_type"
                          value={t}
                          required
                          className="accent-[#3ab5c6]"
                        />
                        {t}
                      </label>
                    ))}
                  </div>
                </div>

                {/* 氏名 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      お名前 <span className="text-rose-500 text-xs ml-1">必須</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="山田 太郎"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">フリガナ</label>
                    <input
                      type="text"
                      name="furigana"
                      placeholder="ヤマダ タロウ"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors"
                    />
                  </div>
                </div>

                {/* メール・電話 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      メールアドレス <span className="text-rose-500 text-xs ml-1">必須</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="example@email.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      電話番号 <span className="text-rose-500 text-xs ml-1">必須</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="090-0000-0000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors"
                    />
                  </div>
                </div>

                {/* 保有資格 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">保有資格</label>
                  <div className="flex flex-wrap gap-4">
                    {["介護職員初任者研修", "実務者研修", "介護福祉士", "普通自動車免許", "その他"].map((q) => (
                      <label key={q} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          name="qualifications"
                          value={q}
                          className="accent-[#3ab5c6]"
                        />
                        {q}
                      </label>
                    ))}
                  </div>
                </div>

                {/* 経験年数 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">介護経験年数</label>
                  <select
                    name="experience_years"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#3ab5c6] transition-colors cursor-pointer bg-white"
                  >
                    <option value="">選択してください</option>
                    <option value="未経験">未経験</option>
                    <option value="1年未満">1年未満</option>
                    <option value="1〜3年">1〜3年</option>
                    <option value="3〜5年">3〜5年</option>
                    <option value="5年以上">5年以上</option>
                  </select>
                </div>

                {/* 自己PR */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">自己PR・志望動機</label>
                  <textarea
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="自己PRや志望動機をご記入ください（500文字以内）"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#3ab5c6] transition-colors resize-none"
                  ></textarea>
                  <p className={`text-xs mt-1 text-right ${charCount >= 500 ? "text-rose-500" : "text-gray-400"}`}>
                    {charCount} / 500文字
                  </p>
                </div>

                {/* Privacy */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="recruit-privacy"
                    name="privacy_agreed"
                    required
                    className="mt-0.5 accent-[#3ab5c6] cursor-pointer"
                  />
                  <label htmlFor="recruit-privacy" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                    <a href="#" className="text-[#3ab5c6] hover:underline">プライバシーポリシー</a>に同意の上、応募します。
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#3ab5c6] text-white text-sm font-medium py-4 rounded-full hover:bg-[#2da0b0] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? "送信中..." : "応募する"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />

      {/* Schema.org JobPosting */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "JobPosting",
          "title": "訪問介護員・サービス提供責任者募集｜株式会社FD",
          "description": "愛知県刈谷市の株式会社FD・ヘルパーステーションみりんでは、訪問介護員およびサービス提供責任者を募集しています。未経験の方も歓迎。資格取得支援・社会保険完備・交通費全額支給。",
          "datePosted": "2026-04-01",
          "validThrough": "2026-12-31",
          "employmentType": ["FULL_TIME", "CONTRACTOR"],
          "hiringOrganization": {
            "@type": "Organization",
            "name": "株式会社FD",
            "sameAs": "https://future-diver.jp/",
            "logo": "https://future-diver.jp/logo.png"
          },
          "jobLocation": {
            "@type": "Place",
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "愛知県",
              "addressLocality": "刈谷市",
              "addressCountry": "JP"
            }
          },
          "baseSalary": {
            "@type": "MonetaryAmount",
            "currency": "JPY",
            "value": {
              "@type": "QuantitativeValue",
              "minValue": 1100,
              "unitText": "HOUR"
            }
          },
          "industry": "医療・福祉",
          "workHours": "シフト制（相談可）",
          "jobBenefits": [
            "社会保険完備",
            "交通費全額支給",
            "資格取得支援制度",
            "入職時研修",
            "スキルアップ研修"
          ],
          "qualifications": "介護職員初任者研修修了以上（または取得見込み）",
          "experienceRequirements": "未経験・ブランクのある方も歓迎",
          "skills": "普通自動車免許（AT限定可）",
          "applicationContact": {
            "@type": "ContactPoint",
            "telephone": "+81-566-68-6220",
            "contactType": "採用担当"
          }
        })}
      </script>
    </div>
  );
}
